<template>
  <div class="page-container">
    <el-card class="search-card">
      <el-row :gutter="20">
        <el-col :span="6">
          <el-input 
            v-model="searchKeyword" 
            placeholder="搜索用户名、昵称或邮箱" 
            clearable
            @clear="handleSearch"
            @keyup.enter.native="handleSearch">
            <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
          </el-input>
        </el-col>
        <el-col :span="4">
          <el-select v-model="statusFilter" placeholder="状态筛选" clearable @change="handleSearch">
            <el-option label="全部" :value="null"></el-option>
            <el-option label="正常" :value="0"></el-option>
            <el-option label="禁用" :value="1"></el-option>
          </el-select>
        </el-col>
        <el-col :span="4">
          <el-button type="primary" @click="handleAdd" icon="el-icon-plus">新增用户</el-button>
        </el-col>
        <el-col :span="4">
          <el-button @click="handleReset" icon="el-icon-refresh">重置</el-button>
        </el-col>
      </el-row>
    </el-card>

    <el-card>
      <el-table :data="tableData" border v-loading="loading">
        <el-table-column prop="id" label="ID" width="80"></el-table-column>
        <el-table-column label="头像" width="80">
          <template slot-scope="scope">
            <el-avatar :src="scope.row.avatar" icon="el-icon-user-solid"></el-avatar>
          </template>
        </el-table-column>
        <el-table-column prop="username" label="用户名" width="120"></el-table-column>
        <el-table-column prop="nickname" label="昵称" width="120"></el-table-column>
        <el-table-column prop="email" label="邮箱" width="180"></el-table-column>
        <el-table-column label="用户类型" width="120">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.userType === 1" type="primary" size="small">后端管理员</el-tag>
            <el-tag v-else type="info" size="small">前端用户</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="角色" width="150">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.role === 1" type="danger" size="small">超级管理员</el-tag>
            <el-tag v-else type="success" size="small">普通用户</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.status === 0" type="success">正常</el-tag>
            <el-tag v-else type="danger">禁用</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="180">
          <template slot-scope="scope">
            {{ formatDate(scope.row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="280" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click="handleAssignRole(scope.row)">分配角色</el-button>
            <el-button size="mini" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button 
              size="mini" 
              :type="scope.row.status === 0 ? 'warning' : 'success'" 
              @click="handleToggleStatus(scope.row)">
              {{ scope.row.status === 0 ? '禁用' : '启用' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        @current-change="handlePageChange"
        @size-change="handleSizeChange"
        :current-page="page.current"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="page.size"
        layout="total, sizes, prev, pager, next, jumper"
        :total="page.total"
        style="margin-top: 20px; text-align: right;">
      </el-pagination>
    </el-card>

    <!-- 编辑/新增对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="600px" :close-on-click-modal="false">
      <el-form :model="form" :rules="rules" ref="userForm" label-width="120px">
        <el-form-item label="用户名" prop="username">
          <el-input v-model="form.username" placeholder="请输入用户名" :disabled="!!form.id"></el-input>
          <div class="tip" v-if="form.id">用户名不可修改</div>
        </el-form-item>
        <el-form-item label="密码" :prop="form.id ? '' : 'password'">
          <el-input type="password" v-model="form.password" :placeholder="form.id ? '不修改则不填' : '请输入密码'"></el-input>
        </el-form-item>
        <el-form-item label="昵称" prop="nickname">
          <el-input v-model="form.nickname" placeholder="请输入昵称"></el-input>
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="form.email" placeholder="请输入邮箱"></el-input>
        </el-form-item>
        <el-form-item label="用户类型" prop="userType">
          <el-radio-group v-model="form.userType">
            <el-radio :label="0">前端用户</el-radio>
            <el-radio :label="1">后端管理员</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio :label="0">正常</el-radio>
            <el-radio :label="1">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSave" :loading="saving">保存</el-button>
      </span>
    </el-dialog>

    <!-- 分配角色对话框 -->
    <el-dialog title="分配角色" :visible.sync="roleDialogVisible" width="500px" :close-on-click-modal="false">
      <div style="margin-bottom: 15px;">
        <strong>用户：</strong>{{ currentUser.nickname }} ({{ currentUser.username }})
      </div>
      <el-checkbox-group v-model="selectedRoles">
        <el-checkbox 
          v-for="role in allRoles" 
          :key="role.id" 
          :label="role.id"
          style="display: block; margin-bottom: 10px;">
          {{ role.roleName }} <span style="color: #909399; font-size: 12px;">({{ role.roleKey }})</span>
        </el-checkbox>
      </el-checkbox-group>
      <span slot="footer">
        <el-button @click="roleDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSaveRoles" :loading="savingRoles">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'UserManage',
  data() {
    return {
      tableData: [],
      loading: false,
      searchKeyword: '',
      statusFilter: null,
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      dialogVisible: false,
      dialogTitle: '',
      form: {
        username: '',
        password: '',
        nickname: '',
        email: '',
        userType: 1,
        status: 0
      },
      saving: false,
      rules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
          { min: 3, max: 20, message: '用户名长度在 3 到 20 个字符', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, max: 20, message: '密码长度在 6 到 20 个字符', trigger: 'blur' }
        ],
        nickname: [
          { required: true, message: '请输入昵称', trigger: 'blur' }
        ],
        email: [
          { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' }
        ]
      },
      roleDialogVisible: false,
      currentUser: {},
      allRoles: [],
      selectedRoles: [],
      savingRoles: false
    }
  },
  mounted() {
    this.loadData()
    this.loadAllRoles()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const res = await this.$axios.get('/admin/users/page', {
          params: {
            current: this.page.current,
            size: this.page.size,
            keyword: this.searchKeyword,
            status: this.statusFilter
          }
        })
        if (res.data.code === 200) {
          this.tableData = res.data.data.records
          this.page.total = res.data.data.total
        }
      } catch (error) {
        this.$message.error('加载失败')
      } finally {
        this.loading = false
      }
    },
    async loadAllRoles() {
      try {
        const res = await this.$axios.get('/admin/role/list')
        if (res.data.code === 200) {
          this.allRoles = res.data.data
        }
      } catch (error) {
        this.$message.error('加载角色列表失败')
      }
    },
    handleSearch() {
      this.page.current = 1
      this.loadData()
    },
    handleReset() {
      this.searchKeyword = ''
      this.statusFilter = null
      this.page.current = 1
      this.loadData()
    },
    handleAdd() {
      this.dialogTitle = '新增用户'
      this.form = {
        username: '',
        password: '',
        nickname: '',
        email: '',
        userType: 1,  // 默认后端管理员
        status: 0
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.userForm && this.$refs.userForm.clearValidate()
      })
    },
    handleEdit(row) {
      this.dialogTitle = '编辑用户'
      this.form = {
        id: row.id,
        username: row.username,
        password: '',  // 密码不显示
        nickname: row.nickname,
        email: row.email,
        userType: row.userType,
        status: row.status
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.userForm && this.$refs.userForm.clearValidate()
      })
    },
    async handleSave() {
      this.$refs.userForm.validate(async (valid) => {
        if (!valid) return

        this.saving = true
        try {
          const data = { ...this.form }
          
          // 编辑时如果密码为空，不传密码字段
          if (data.id && !data.password) {
            delete data.password
          }
          
          if (data.id) {
            await this.$axios.put(`/admin/users/${data.id}`, data)
          } else {
            await this.$axios.post('/admin/users', data)
          }
          
          this.$message.success('保存成功')
          this.dialogVisible = false
          this.loadData()
        } catch (error) {
          this.$message.error(error.response?.data?.message || '保存失败')
        } finally {
          this.saving = false
        }
      })
    },
    handlePageChange(page) {
      this.page.current = page
      this.loadData()
    },
    handleSizeChange(size) {
      this.page.size = size
      this.page.current = 1
      this.loadData()
    },
    handleToggleStatus(row) {
      const newStatus = row.status === 0 ? 1 : 0
      const action = newStatus === 1 ? '禁用' : '启用'
      
      this.$confirm(`确定${action}用户"${row.nickname}"?`, '提示', {
        type: 'warning'
      }).then(async () => {
        try {
          await this.$axios.put(`/admin/users/${row.id}/status`, {
            status: newStatus
          })
          this.$message.success(`${action}成功`)
          this.loadData()
        } catch (error) {
          this.$message.error(`${action}失败`)
        }
      }).catch(() => {})
    },
    async handleAssignRole(row) {
      this.currentUser = row
      this.roleDialogVisible = true
      
      // 加载用户已有角色
      try {
        const res = await this.$axios.get(`/admin/users/${row.id}/roles`)
        if (res.data.code === 200) {
          this.selectedRoles = res.data.data
        }
      } catch (error) {
        this.$message.error('加载用户角色失败')
      }
    },
    async handleSaveRoles() {
      this.savingRoles = true
      try {
        await this.$axios.put(`/admin/users/${this.currentUser.id}/roles`, {
          roleIds: this.selectedRoles
        })
        this.$message.success('角色分配成功')
        this.roleDialogVisible = false
        this.loadData()
      } catch (error) {
        this.$message.error(error.response?.data?.message || '角色分配失败')
      } finally {
        this.savingRoles = false
      }
    },
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleString('zh-CN')
    }
  }
}
</script>

<style scoped>
.page-container {
  padding: 20px;
  background: transparent;
  min-height: calc(100vh - 60px);
}

.search-card {
  margin-bottom: 20px;
}

.tip {
  margin-top: 5px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}
</style>
